import React, { useEffect, useState } from 'react';
import { getOrganizationBankPaySetting } from '~/javascripts/admin/actions/organization';
import { getPrivateMoney, updatePrivateMoney } from '~/javascripts/admin/actions/private-moneys';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import EditPrivateMoney, { PrivateMoneyFormValues } from '../EditPrivateMoney';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: 'マネー設定', href: '', isCurrentPage: true },
];

const toFormValues = (
  money: Awaited<ReturnType<typeof getPrivateMoney>>
): PrivateMoneyFormValues => {
  const creditCardTopupMethod = money.topup_methods?.find(
    (method) => method.type === 'credit-card'
  );
  const bankPayTopupMethod = money.topup_methods?.find((method) => method.type === 'paytree-bank');
  const sevenBankTopupMethod = money.topup_methods?.find(
    (method) => method.type === 'sevenbank-atm'
  );

  return {
    organization_code: money.organization?.code,
    name: money.name,
    furigana: money.furigana,
    type: money.type,
    can_use_c2c_transfer: money.can_use_c2c_transfer,
    expiration_type: money.expiration_type,
    expires_in: {
      amount: money.expires_in_months ?? money.expires_in_days ?? 6,
      unit: money.expires_in_months ? 'months' : 'days',
    },
    expires_at: money.expires_at,
    point_expiration_type: money.point_expiration_type,
    point_expires_in: {
      amount: money.point_expires_in_months ?? money.point_expires_in_days ?? 6,
      unit: money.point_expires_in_months ? 'months' : 'days',
    },
    point_expires_at: money.point_expires_at,
    max_balance: money.max_balance,
    transfer_limit: money.transfer_limit,
    money_topup_transfer_limit: money.money_topup_transfer_limit,
    total_topup_limit: money.total_topup_limit ?? {
      type: null,
      amount: null,
      started_at: null,
    },
    terms: money.legal_notes?.terms,
    privacy_policy: money.legal_notes?.privacy_policy,
    payment_act: money.legal_notes?.payment_act,
    commercial_act: money.legal_notes?.commercial_act,
    access_limitation: money.access_limitation,
    display_money_and_point: money.display_money_and_point,
    unit: money.unit,
    description: money.description,
    oneline_message: money.oneline_message,
    square_small_image: {
      file: null,
      src: money.images?.['300x300'] ?? null,
    },
    payment_sound: {
      file: null,
      src: money.sounds?.payment ?? null,
    },
    can_use_credit_card: money.can_use_credit_card,
    require_3ds_challenge: money.require_3ds_challenge ?? false,
    credit_card_daily_cap: creditCardTopupMethod?.additional_info?.credit_card_daily_cap ?? null,
    credit_card_monthly_cap:
      creditCardTopupMethod?.additional_info?.credit_card_monthly_cap ?? null,
    topup_method_amounts_for_credit_card: creditCardTopupMethod?.amounts ?? [],
    topup_method_amount_range_for_credit_card: creditCardTopupMethod?.range ?? ['', ''],
    can_use_bank_pay: !!bankPayTopupMethod,
    bank_pay_p_id: bankPayTopupMethod?.additional_info?.p_id ?? '',
    bank_pay_iss_type: bankPayTopupMethod?.additional_info?.iss_type?.substring(3) ?? '',
    bank_pay_store_code: bankPayTopupMethod?.additional_info?.store_code ?? '',
    bank_pay_bankbook_merchant_name:
      bankPayTopupMethod?.additional_info?.bankbook_merchant_name ?? '',
    can_use_seven_bank_atm: !!sevenBankTopupMethod,
    seven_bank_issuer_code: sevenBankTopupMethod?.additional_info?.issuer_code ?? '',
    seven_bank_issuer_name: sevenBankTopupMethod?.additional_info?.issuer_name ?? '',
    seven_bank_money_name: sevenBankTopupMethod?.additional_info?.money_name ?? '',
    topup_method_amounts_for_seven_bank_atm: sevenBankTopupMethod?.amounts ?? [],
    is_topup_quota_available: money.is_topup_quota_available,
    base_color: money.kiosk_colors?.base_color ?? '#000000',
    button_foreground_color: money.kiosk_colors?.button_foreground_color ?? '#000000',
    button_background_color: money.kiosk_colors?.button_background_color ?? '#000000',
    custom_domain_name: money.custom_domain_name ?? '',
    card_image: {
      file: null,
      src: money.images?.card ?? null,
    },
    use_webhook_callback_url: !!money.transaction_webhook_callback_url,
    transaction_webhook_callback_url: money.transaction_webhook_callback_url,
    use_itrust_tenent: !!money.itrust_tenant_code,
    itrust_tenant_code: money.itrust_tenant_code,
    resident_address_pattern: money.resident_address_pattern,
    identification_validity_days: money.identification_validity_days,
    use_fallback_payment: !!money.fallback_private_money_uuid,
    fallback_private_money_uuid: money.fallback_private_money_uuid ?? null,
  };
};

const PARTIAL_EDITABLE_FIELDS = [
  'access_limitation',
  'oneline_message',
  'description',
  'unit',
  'card_image',
  'square_small_image',
  'payment_sound',
  'can_use_c2c_transfer',
  'use_webhook_callback_url',
  'transaction_webhook_callback_url',
] as const;

const SettingsMoney = () => {
  const adminUser = useAppSelector((state) => state.user);
  const [selectedMoneyFormValues, setSelectedMoneyFormValues] = useState<PrivateMoneyFormValues>();

  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const allowedPrivateMoneys = adminUser.isServiceUser() ? [] : adminUser.issuedMoneys();
  const isSelectedMoneyAllowed =
    adminUser.isServiceUser() || allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);

  const canFullUpdate = adminUser.role === 'SERVICE-MANAGER';
  const canPartialUpdate = adminUser.role === 'SERVICE-STAFF' || adminUser.isOrgUser();

  const getEditableFields = (): 'all' | (keyof PrivateMoneyFormValues)[] => {
    if (canFullUpdate) return 'all';
    if (canPartialUpdate) return [...PARTIAL_EDITABLE_FIELDS] as (keyof PrivateMoneyFormValues)[];
    return [];
  };

  useEffect(() => {
    getPrivateMoney(userPrivateMoney.id)
      .then((json) => {
        setSelectedMoneyFormValues(toFormValues(json));
        return json;
      })
      .then((money) => getOrganizationBankPaySetting(money.organization.code))
      .then((bankPaySetting) => {
        setSelectedMoneyFormValues((prev) =>
          prev
            ? {
                ...prev,
                bank_pay_p_id: bankPaySetting.p_id,
                bank_pay_iss_type: bankPaySetting.iss_type.substring(3),
                bank_pay_bankbook_merchant_name: bankPaySetting.bankbook_merchant_name,
              }
            : prev
        );
      });
  }, [userPrivateMoney]);

  const getPartialEditableFields = (values: PrivateMoneyFormValues) => {
    return Object.keys(values).reduce(
      (acc, key) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (PARTIAL_EDITABLE_FIELDS.includes(key as any)) {
          acc[key] = values[key];
        }
        return acc;
      },
      {} as {
        [key in (typeof PARTIAL_EDITABLE_FIELDS)[number]]: PrivateMoneyFormValues[key];
      }
    );
  };

  const transformFormValues = (values: PrivateMoneyFormValues) => {
    if (canFullUpdate) {
      const {
        expires_in,
        point_expires_in,
        total_topup_limit,
        square_small_image,
        card_image,
        payment_sound,
        expires_at,
        point_expires_at,
        can_use_seven_bank_atm,
        seven_bank_issuer_code,
        seven_bank_issuer_name,
        seven_bank_money_name,
        topup_method_amounts_for_seven_bank_atm,
        can_use_bank_pay,
        bank_pay_p_id,
        bank_pay_iss_type,
        bank_pay_store_code,
        bank_pay_bankbook_merchant_name,
        credit_card_daily_cap,
        credit_card_monthly_cap,
        use_webhook_callback_url,
        transaction_webhook_callback_url,
        use_itrust_tenent,
        itrust_tenant_code,
        resident_address_pattern,
        identification_validity_days,
        use_fallback_payment,
        fallback_private_money_uuid,
        ...rest
      } = values;

      // string of 'null' will be set in form data but null keyword will be removed
      const transformedValues = {
        ...rest,
        expires_in_months: expires_in.unit === 'months' ? expires_in.amount : 'null',
        expires_in_days: expires_in.unit === 'days' ? expires_in.amount : 'null',
        expires_at: expires_at ?? 'null',
        point_expires_in_months:
          point_expires_in.unit === 'months' ? point_expires_in.amount : 'null',
        point_expires_in_days: point_expires_in.unit === 'days' ? point_expires_in.amount : 'null',
        point_expires_at: point_expires_at ?? 'null',
        total_topup_limit_type: total_topup_limit.type ?? 'null',
        total_topup_limit_amount: total_topup_limit.amount ?? 'null',
        total_topup_limit_started_at:
          total_topup_limit.type !== 'range' ? 'null' : total_topup_limit.started_at,
        square_small_image: square_small_image.file,
        card_image: card_image.file,
        payment_sound: payment_sound.file,
        bank_pay_p_id: can_use_bank_pay ? bank_pay_p_id : 'null',
        bank_pay_iss_type: can_use_bank_pay ? `ISS${bank_pay_iss_type}` : 'null',
        bank_pay_store_code: can_use_bank_pay ? bank_pay_store_code : 'null',
        bank_pay_bankbook_merchant_name: can_use_bank_pay
          ? bank_pay_bankbook_merchant_name
          : 'null',
        seven_bank_issuer_code: can_use_seven_bank_atm ? seven_bank_issuer_code : 'null',
        seven_bank_issuer_name: can_use_seven_bank_atm ? seven_bank_issuer_name : 'null',
        seven_bank_money_name: can_use_seven_bank_atm ? seven_bank_money_name : 'null',
        topup_method_amounts_for_seven_bank_atm: can_use_seven_bank_atm
          ? topup_method_amounts_for_seven_bank_atm
          : null,
        credit_card_daily_cap: credit_card_daily_cap ?? 'null',
        credit_card_monthly_cap: credit_card_monthly_cap ?? 'null',
        transaction_webhook_callback_url: use_webhook_callback_url
          ? transaction_webhook_callback_url
          : 'null',
        itrust_tenant_code: use_itrust_tenent ? itrust_tenant_code : 'null',
        resident_address_pattern: use_itrust_tenent ? resident_address_pattern : 'null',
        identification_validity_days: use_itrust_tenent
          ? identification_validity_days ?? 'null'
          : 'null',
        fallback_private_money_uuid: use_fallback_payment
          ? fallback_private_money_uuid ?? 'null'
          : 'null',
      };
      return transformedValues;
    }
    if (canPartialUpdate) {
      const partialEditableFields = getPartialEditableFields(values);
      const {
        square_small_image,
        card_image,
        payment_sound,
        use_webhook_callback_url,
        transaction_webhook_callback_url,
        ...rest
      } = partialEditableFields;
      const transformedValues = {
        ...rest,
        square_small_image: square_small_image.file,
        card_image: card_image.file,
        payment_sound: payment_sound.file,
        transaction_webhook_callback_url: use_webhook_callback_url
          ? transaction_webhook_callback_url
          : 'null',
      };
      return { ...transformedValues };
    }
    return {};
  };

  const onConfirm = async (formValues: PrivateMoneyFormValues) => {
    const transformedValues = transformFormValues(formValues);

    const formData = new FormData();
    formData.append('_csrf_token', window._csrf_token);
    Object.keys(transformedValues).forEach((key) => {
      if (transformedValues[key] != null) {
        formData.append(key, transformedValues[key]);
      }
    });

    await updatePrivateMoney(userPrivateMoney.id, formData);
  };

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      {isSelectedMoneyAllowed ? (
        <>
          <Headings.h5>マネー設定</Headings.h5>
          <SelectedPrivateMoneyDisplay wrapperStyles={{ marginBottom: '16px' }} />
          {selectedMoneyFormValues ? (
            <EditPrivateMoney
              mode="edit"
              moneyFormValues={selectedMoneyFormValues}
              onConfirm={onConfirm}
              allowedInputs={getEditableFields()}
              disabled={!canFullUpdate && !canPartialUpdate}
            />
          ) : (
            <Loader type="Oval" height={100} width={100} />
          )}
        </>
      ) : (
        <>
          <SelectedPrivateMoneyDisplay
            highlightHeader
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <UserPrivateMoneyNotAllowed />
        </>
      )}
    </>
  );
};

export default SettingsMoney;
